import styled from '@emotion/styled';
import { DSButton } from '@hundred5/design-system';
import React from 'react';

import {
  BillingCouponForm,
  BillingDetailsEdit,
  BillingSelectedPlanInfo,
  BillingSelectedPlanPriceConfirmation,
  PlanCode,
  useFreeTrial,
  useHandleBillingError,
} from '@/features/billing';
import { Loader } from '@/features/common';
import { useNotifications } from '@/features/notifications';
import { trackSwitchPlan } from '@/googleTagManager';
import { useWorkspaceId } from '@/hooks/router';

import bikeForward from '../assets/bike-forward.svg';

import { ME_QUERY_KEY } from '@/features/me';
import { usePutSubscriptionMutation } from '@/queries/subscriptions/mutations';
import { useGetCustomer } from '@/queries/subscriptions/queries';
import { usePostFinishTrialMutation } from '@/queries/trials/mutations';
import { queryClient } from '@/utils/reactQuery';
import { Content, Header, Illustration } from '.';

interface UpgradeProps {
  plan?: PlanCode;
  onGoBack?: () => void;
}

export const Upgrade = ({ plan, onGoBack }: UpgradeProps) => {
  const { addNotification } = useNotifications();
  const { handleUnknownError } = useHandleBillingError();
  const { lastActiveTrial } = useFreeTrial();
  const workspaceId = useWorkspaceId();
  const subscriptionMutation = usePutSubscriptionMutation();
  const finishDowngradeTrial = usePostFinishTrialMutation();

  const { data: customer, isFetched: loadedInitialCustomer } = useGetCustomer(
    Number(workspaceId),
    !!plan
  );
  const needsBillingInfo = !customer || !customer.card;

  const handleSubscribe = () => {
    if (!plan) {
      return;
    }

    subscriptionMutation.mutate(
      {
        workspace_id: Number(workspaceId),
        data: { subscriptionID: plan },
      },
      {
        onSuccess: () => {
          if (lastActiveTrial) {
            finishDowngradeTrial.mutate(
              {
                workspace_id: Number(workspaceId),
                trial_id: Number(lastActiveTrial.id),
                data: { showDowngrade: false },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
                },
              }
            );
          }

          trackSwitchPlan(plan);
          addNotification({ type: 'plan_updated' });
          queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        },
        onError: (error) => {
          handleUnknownError('Failed to change subscription', error);
        },
      }
    );
  };

  return (
    <>
      <Content>
        <Header>
          <h1>Checkout</h1>
        </Header>

        {!loadedInitialCustomer ? (
          <Loader />
        ) : (
          <>
            <BillingSelectedPlanInfo plan={plan} />

            {needsBillingInfo ? (
              <BillingDetailsEdit />
            ) : (
              <>
                <BillingSelectedPlanPriceConfirmation plan={plan} />
                <BillingCouponForm />
              </>
            )}

            <Footer>
              <DSButton variant="secondary" onClick={onGoBack}>
                Go back
              </DSButton>

              {!needsBillingInfo && (
                <DSButton
                  type="submit"
                  variant="primary-purple"
                  disabled={subscriptionMutation.isPending}
                  onClick={handleSubscribe}
                >
                  {subscriptionMutation.isPending
                    ? 'Completing...'
                    : 'Complete purchase'}
                </DSButton>
              )}
            </Footer>
          </>
        )}
      </Content>

      <Illustration src={bikeForward} alt="Go forward" centered />
    </>
  );
};

const Footer = styled.footer`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 36px;
  gap: 8px;
  flex-flow: wrap;
`;
